import { Flex, Line } from 'components';
import { IGenericModal, Modal } from 'components/Modal/Modal';
import { FieldLabel, Radio } from 'components/_form';
import { ErrorText } from 'components/_form/ErrorText';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { ApprovalSchemeApproversSection } from 'components/ApprovalSchemeApproversSection/ApprovalSchemeApproversSection';
import { useExecutionProgressTrackingsQuery } from 'hooks/queries/useExecutionProgressTrackingsQuery';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { IApprovalSchemeFormValues } from 'types/approver';
import { mapUserFunctionsToOptions } from 'utilities/userFunctions';

export type AuditFormApprovalSchemeValues = IApprovalSchemeFormValues;

interface Props extends IGenericModal {
  defaultValues?: AuditFormApprovalSchemeValues;
  onSave: (values: AuditFormApprovalSchemeValues) => void;
}

export const AuditFormApprovalSchemeModal = ({
  isOpen,
  onCancelClick,
  defaultValues,
  onSave
}: Props) => {
  const { t } = useTranslation();
  const { data: executionProgressTrackings = [] } =
    useExecutionProgressTrackingsQuery();
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const userFunctionsList = useMemo(
    () => mapUserFunctionsToOptions(userFunctionsData),
    [userFunctionsData]
  );

  const defaultTrackingId = useMemo(() => {
    const pdca = executionProgressTrackings.find(
      (tracking) => tracking.kind.toUpperCase() === 'PDCA'
    );
    return (
      defaultValues?.execution_progress_tracking_id ??
      pdca?.id ??
      executionProgressTrackings[0]?.id
    );
  }, [defaultValues?.execution_progress_tracking_id, executionProgressTrackings]);

  const methods = useForm<AuditFormApprovalSchemeValues>({
    defaultValues: {
      execution_progress_tracking_id: defaultTrackingId,
      in_this_order: defaultValues?.in_this_order ?? false,
      approvers_attributes: defaultValues?.approvers_attributes ?? []
    }
  });

  const { control, handleSubmit, reset, watch } = methods;
  const selectedTrackingId = watch('execution_progress_tracking_id');

  const approvalTrackings = useMemo(
    () =>
      executionProgressTrackings.filter(
        (tracking) => tracking.id === selectedTrackingId
      ),
    [executionProgressTrackings, selectedTrackingId]
  );

  useEffect(() => {
    if (!isOpen) return;

    reset({
      execution_progress_tracking_id: defaultTrackingId,
      in_this_order: defaultValues?.in_this_order ?? false,
      approvers_attributes: defaultValues?.approvers_attributes ?? []
    });
  }, [isOpen, defaultTrackingId, defaultValues, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      headerTitle={t('administrationAuditsView.createApprovalScheme')}
      mainButton={{
        action: handleSubmit((data) => {
          onSave(data);
          onCancelClick();
        }),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
    >
      <FormProvider {...methods}>
        <form>
          <Flex flexDirection="column" gap="16px">
            <Flex alignItems="center" flexDirection={['column', 'row']}>
              <Flex width={['100%', '20%']}>
                <FieldLabel>
                  {t('administrationTasksView.followingStatusBy')}
                </FieldLabel>
              </Flex>
              <Flex width={['100%', '80%']} flexDirection="column">
                <Controller
                  control={control}
                  name="execution_progress_tracking_id"
                  render={({ field: { value, onChange } }) => (
                    <Flex
                      width="100%"
                      gap="24px"
                      alignItems="center"
                      justifyContent="space-evenly"
                      flexWrap="wrap"
                    >
                      {executionProgressTrackings.map(
                        (executionProgressTracking) => (
                          <Flex
                            key={`AuditFormApprovalSchemeModal-tracking-${executionProgressTracking.id}`}
                            alignItems="center"
                            gap="8px"
                            flex="1"
                            justifyContent="center"
                            minWidth="180px"
                          >
                            <Radio
                              checked={value === executionProgressTracking.id}
                              onClick={() => {
                                onChange(executionProgressTracking.id);
                              }}
                            />
                            <ExecutionProgressTracking
                              activeAll
                              executionProgressTracking={
                                executionProgressTracking
                              }
                            />
                          </Flex>
                        )
                      )}
                    </Flex>
                  )}
                />
                {!selectedTrackingId && (
                  <ErrorText>
                    {t('administrationTasksView.selectStatusTrackingFirst')}
                  </ErrorText>
                )}
              </Flex>
            </Flex>

            <Line variant="secondary" />

            <ApprovalSchemeApproversSection
              selectedTrackingId={selectedTrackingId}
              executionProgressTrackings={approvalTrackings}
              userFunctionsList={userFunctionsList}
            />
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
